export const fieldRequiredError = (value: string) => {
  if (value === "") {
    return "This field is required";
  }
  return undefined;
};

const isAustralia = (country: string | null | undefined): boolean => {
  const lowerCaseCountry = country?.toLowerCase();
  return lowerCaseCountry === "australia" || lowerCaseCountry === "au";
};

const AUSTRALIA_STREET_MAX_LENGTH = 40;

interface AustraliaStreetValidationResult {
  street1Error: string | undefined;
  street2Error: string | undefined;
  hasBlockingError: boolean;
}

const validateAustraliaStreets = (
  street1: string | null | undefined,
  street2: string | null | undefined,
): AustraliaStreetValidationResult => {
  const street1Length = (street1 ?? "").length;
  const street2Length = (street2 ?? "").length;

  const street1Error =
    street1Length > AUSTRALIA_STREET_MAX_LENGTH
      ? "Must be less than 40 characters. Use additional field below if needed."
      : undefined;
  const street2Error =
    street2Length > AUSTRALIA_STREET_MAX_LENGTH
      ? "Must be less than 40 characters."
      : undefined;

  return {
    street1Error,
    street2Error,
    hasBlockingError: street1Error !== undefined || street2Error !== undefined,
  };
};

export const ausPostError = (value: string, country: string) => {
  if (!isAustralia(country)) {
    return undefined;
  }
  const validation = validateAustraliaStreets(value, undefined);
  return validation.street1Error;
};

export enum AddressValidationErrorType {
  AustraliaStreetLength = "australia_street_length",
}

export interface BlockingAddressError {
  type: AddressValidationErrorType;
  message: string;
  addressType?: string;
}

export const checkBlockingAddressErrors = (
  street1?: string | null,
  street2?: string | null,
  country?: string | null,
  addressType?: string,
): BlockingAddressError | null => {
  if (!isAustralia(country)) {
    return null;
  }

  const validation = validateAustraliaStreets(street1, street2);

  if (!validation.hasBlockingError) {
    return null;
  }

  if (validation.street1Error) {
    return {
      type: AddressValidationErrorType.AustraliaStreetLength,
      message:
        "Street address line 1 must be 40 characters or less. Use street address line 2 if needed.",
      addressType,
    };
  }

  if (validation.street2Error) {
    return {
      type: AddressValidationErrorType.AustraliaStreetLength,
      message: "Street address line 2 must be 40 characters or less.",
      addressType,
    };
  }

  return null;
};

export const getAustraliaStreetValidationErrors = (
  street1?: string | null,
  street2?: string | null,
  country?: string | null,
): { street1: string | undefined; street2: string | undefined } => {
  if (!isAustralia(country)) {
    return { street1: undefined, street2: undefined };
  }

  const validation = validateAustraliaStreets(street1, street2);
  return {
    street1: validation.street1Error,
    street2: validation.street2Error,
  };
};
